import React, { useState, useEffect } from 'react'
import { Link, Stack, IconButton, InputAdornment, TextField, Box } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import * as yup from 'yup'
import i18n from '../../../i18n'
import { useAuth } from '../../../hooks/useAuth'
import Iconify from '../../../components/Iconify'
import { storageEmailGet, storageEmailSave } from 'storage/storageEmail'
import { useAppDispatch } from 'redux/hooks'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'

export interface SignInFormProps {
  email: string
  password: string
}

const signInSchema = yup.object({
  email: yup
    .string()
    .trim()
    .lowercase()
    .required(() => i18n.t<string>('general.validator.empty'))
    .email(() => i18n.t<string>('general.validator.invalidEmail')),
  password: yup
    .string()
    .trim()
    .required(() => i18n.t<string>('general.validator.empty'))
    .min(6, () => i18n.t<string>('general.validator.minCharacter', { number: 6 }))
})

export default function LoginForm () {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { signIn } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const { control, handleSubmit, setValue, formState: { errors } } = useForm<SignInFormProps>({
    resolver: yupResolver(signInSchema)
  })

  useEffect(() => {
    const storageEmail = storageEmailGet()
    if (storageEmail !== null) {
      setValue('email', storageEmail)
    }
  }, [])

  const handleSignIn = async (data: SignInFormProps): Promise<void> => {
    const { email, password } = data
    try {
      setIsLoading(true)
      await signIn(email, password)
      storageEmailSave(email)
    } catch (error) {
      if (process.env.REACT_APP_ENVIRONMENT === 'dev') {
        console.error(error)
      }
      dispatch(showMessage({ message: `${t('account.Invalid_credentials')}`, type: 'error' }))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Stack spacing={3}>
        <Controller
          control={control}
          defaultValue=''
          name="email"
          render={({ field: { onChange, value } }) => (
            <TextField
              placeholder={t<string>('signIn.field.email.placeholder')}
              label={t<string>('signIn.field.email.label')}
              onChange={onChange}
              value={value}
              helperText={errors.email?.message}
              error={errors.email?.message !== undefined}
            />
          )} />
        <Controller
          control={control}
          name="password"
          render={({ field: { onChange } }) => (
            <TextField
              name="password"
              label={t<string>('signIn.field.password.label')}
              type={showPassword ? 'text' : 'password'}
              onChange={onChange}
              helperText={errors.password?.message}
              error={errors.password?.message !== undefined}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          )} />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
        <Box />
        <Link variant="subtitle2" href="/forgot" underline="hover">
          {t('signIn.link.forgotPassword')}
        </Link>
      </Stack>

      <LoadingButton fullWidth size="large" type="submit" variant="contained" onClick={handleSubmit(handleSignIn)}
        loading={isLoading}>
        {t('signIn.btn.login')}
      </LoadingButton>
    </>
  )
}
